import { useEffect, useState } from 'react'

export type GoogleFontLoadStatus = 'idle' | 'loading' | 'loaded' | 'error'

export interface UseGoogleFontResult {
  status: GoogleFontLoadStatus
}

const LOAD_TIMEOUT_MS = 5000

/**
 * Tracks whether the tenant-chosen portal font is usable.
 *
 * It no longer injects a stylesheet from fonts.googleapis.com (PV-25): that request told Google which
 * portal each visitor was on. The fonts the picker offers are either hosted by this app (Inter, Fraunces,
 * JetBrains Mono, see public/fonts/fonts.css) or system fonts, so there is nothing to download. A family
 * saved earlier that is not available falls back to the browser's default through the font stack, and the
 * status becomes 'error' once the CSS Font Loading API gives up or times out.
 */
export function useGoogleFont(fontFamily: string | null | undefined): UseGoogleFontResult {
  const [status, setStatus] = useState<GoogleFontLoadStatus>('idle')

  useEffect(() => {
    if (!fontFamily) {
      setStatus('idle')
      return
    }

    setStatus('loading')
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const safeFamily = fontFamily.replace(/["\\]/g, '')
    const loadPromise = document.fonts
      ? document.fonts.load(`400 16px "${safeFamily}"`)
      : Promise.resolve([])
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      timeoutId = setTimeout(() => reject(new Error('font load timed out')), LOAD_TIMEOUT_MS)
    })

    Promise.race([loadPromise, timeoutPromise])
      .then(() => {
        if (!cancelled) setStatus('loaded')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
      .finally(() => {
        if (timeoutId) clearTimeout(timeoutId)
      })

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [fontFamily])

  return { status }
}
